import { useCallback, useMemo, useState } from 'react';

import dayjs from 'dayjs';

import {
  BottomSheet,
  Button,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  IconButton,
  Text,
} from '@thefarmersfront/kitchen-mobile';

import { DATE_FORMAT_TO_MONTH, DATE_RANGE_MAX_DATE, DATE_RANGE_MIN_DATE } from '~/constants';
import { getFormattedDate } from '~/utils';

import styles from './MonthPicker.module.scss';

/** @description 월 선택 컴포넌트 */
const MonthPicker = ({ value, onChange }: { value?: string; onChange: (value: string) => void }) => {
  const date = value ? new Date(value) : new Date();

  const [showMonthPickerBottomSheet, setShowMonthPickerBottomSheet] = useState(false);

  const handleDateChange = (value: Date) => {
    onChange(getFormattedDate({ date: dayjs(value), format: DATE_FORMAT_TO_MONTH }));
  };

  return (
    <>
      <button className={styles['month-picker']} onClick={() => setShowMonthPickerBottomSheet(true)}>
        <Text size="xxlarge" weight="semi-bold">
          {getFormattedDate({ date: dayjs(date), format: 'YYYY년 M월' })}
        </Text>
        {showMonthPickerBottomSheet ? <ChevronUpIcon size={20} /> : <ChevronDownIcon size={20} />}
      </button>
      {showMonthPickerBottomSheet && (
        <MonthPickerBottomSheet
          value={date}
          open={showMonthPickerBottomSheet}
          onClose={() => setShowMonthPickerBottomSheet(false)}
          onChange={handleDateChange}
        />
      )}
    </>
  );
};

const MonthPickerBottomSheet = ({
  value,
  open,
  onClose,
  onChange,
}: {
  value: Date;
  open: boolean;
  onClose: () => void;
  onChange: (value: Date) => void;
}) => {
  const [selectedValue, setSelectedValue] = useState<Date>(value);

  const {
    currentPageYear,
    isPrevYearDisabled,
    isNextYearDisabled,
    isMonthDisabled,
    isMonthSelected,
    resetPage,
    setPrevPage,
    setNextPage,
  } = useCalendarPageInfo({
    currentDate: selectedValue,
    minDate: dayjs(DATE_RANGE_MIN_DATE).toDate(),
    maxDate: dayjs(DATE_RANGE_MAX_DATE).toDate(),
  });

  return (
    <BottomSheet
      id="month-picker-bottom-sheet"
      open={open}
      onClose={onClose}
      buttonConfig={{
        buttonType: 'alert',
        confirmText: getFormattedDate({ date: dayjs(selectedValue), format: 'YYYY년 M월' }),
        onConfirm: () => {
          onClose();
          onChange(selectedValue);
        },
      }}
    >
      <div className={styles['month-picker-title']} style={{ gap: '7%' }}>
        <IconButton color="secondary" disabled={isPrevYearDisabled} onClick={setPrevPage}>
          <ChevronLeftIcon />
        </IconButton>
        <Text size="xxlarge" weight="semi-bold">
          {currentPageYear}년
        </Text>
        <IconButton color="secondary" disabled={isNextYearDisabled} onClick={setNextPage}>
          <ChevronRightIcon />
        </IconButton>
        <Button
          className={styles['month-picker-today-button']}
          variant="outlined"
          color="tertiary"
          size="medium"
          onClick={() => {
            setSelectedValue(dayjs().toDate());
            resetPage();
          }}
        >
          이번달
        </Button>
      </div>
      <div className={styles['month-picker-content']}>
        {Array.from({ length: 12 }, (_, index) => {
          const disabled = isMonthDisabled(dayjs(`${currentPageYear}-${index + 1}-01`).toDate());
          return (
            <Button
              key={index}
              variant="outlined"
              className={disabled ? styles['month-picker-disabled'] : ''}
              color={isMonthSelected(index) ? 'secondary' : 'tertiary'}
              onClick={() => {
                setSelectedValue(dayjs(`${currentPageYear}-${index + 1}-01`).toDate());
                resetPage();
              }}
              disabled={disabled}
            >
              <Text size="xlarge" weight="semi-bold">
                {index + 1}월
              </Text>
            </Button>
          );
        })}
      </div>
    </BottomSheet>
  );
};

const useCalendarPageInfo = ({
  currentDate,
  minDate,
  maxDate,
}: {
  currentDate: Date;
  minDate?: Date;
  maxDate?: Date;
}) => {
  const [page, setPage] = useState<number>(0);

  const currentPageYear = useMemo(() => {
    return dayjs(currentDate).add(page, 'year').year();
  }, [currentDate, page]);

  const isPrevYearDisabled = useMemo(() => {
    if (!minDate) return false;

    return dayjs(currentDate)
      .add(page - 1, 'year')
      .isBefore(dayjs(minDate), 'year');
  }, [currentDate, page, minDate]);

  const isNextYearDisabled = useMemo(() => {
    if (!maxDate) return false;
    return dayjs(currentDate)
      .add(page + 1, 'year')
      .isAfter(dayjs(maxDate), 'year');
  }, [currentDate, page, maxDate]);

  const isMonthDisabled = useCallback(
    (selectedDate: Date) => {
      const isMinYear = currentPageYear === dayjs(minDate).year();
      const isMaxYear = currentPageYear === dayjs(maxDate).year();

      return (
        (isMinYear && dayjs(selectedDate).isBefore(dayjs(minDate), 'month')) ||
        (isMaxYear && dayjs(selectedDate).isAfter(dayjs(maxDate), 'month'))
      );
    },
    [currentPageYear, minDate, maxDate]
  );

  const isMonthSelected = useCallback(
    (month: number) => {
      return month === dayjs(currentDate).month() && currentPageYear === currentDate.getFullYear();
    },
    [currentDate, currentPageYear]
  );

  const resetPage = () => {
    setPage(0);
  };

  const setPrevPage = () => {
    setPage((prev) => prev - 1);
  };

  const setNextPage = () => {
    setPage((prev) => prev + 1);
  };

  return {
    currentPageYear,
    isPrevYearDisabled,
    isNextYearDisabled,
    isMonthDisabled,
    isMonthSelected,
    resetPage,
    setPrevPage,
    setNextPage,
  };
};

export default MonthPicker;
